import { useEffect, useRef } from "react";
import type { RunSummary } from "../lib/types";
import { type SortDir } from "../lib/bundle";
import { Mark } from "./Mark";

type Props = {
  runs: RunSummary[];
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
  sortDir: SortDir;
  onChangeSortDir: (d: SortDir) => void;
  query: string;
  matchCounts: Map<string, number>; // runId → search hit count (0 when no query)
  totalAvailableRuns: number;       // count BEFORE search filter applied
};

// Left rail — runs in sorted + searched order. Each row: stable ordinal
// (#1 = earliest run of the day, independent of display sort), HH:MM:SS,
// short id, per-action count badges. While searching, the short id and
// times stay normal but a "N hits" sub-badge appears on rows that matched.
export function RunList({
  runs,
  selectedRunId,
  onSelect,
  sortDir,
  onChangeSortDir,
  query,
  matchCounts,
  totalAvailableRuns,
}: Props) {
  const isSearching = query.trim().length > 0;

  // Scroll the selected run into view whenever the selection changes, so a jump
  // from the Feeds panel (or any programmatic select) brings the row on-screen.
  // "nearest" means no movement when the row is already visible.
  const selectedItemRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    selectedItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedRunId]);

  return (
    <aside className="runlist">
      <div className="runlist__header">
        <div className="runlist__header-top">
          <h3>
            Runs ({runs.length}{isSearching && runs.length !== totalAvailableRuns ? ` of ${totalAvailableRuns}` : ""})
          </h3>
        </div>
        <div className="runlist__sort">
          <button
            className={`runlist__sort-btn ${sortDir === "desc" ? "runlist__sort-btn--active" : ""}`}
            onClick={() => onChangeSortDir("desc")}
          >
            Latest first
          </button>
          <button
            className={`runlist__sort-btn ${sortDir === "asc" ? "runlist__sort-btn--active" : ""}`}
            onClick={() => onChangeSortDir("asc")}
          >
            Oldest first
          </button>
        </div>
      </div>
      <ul className="runlist__items">
        {runs.length === 0 ? (
          <li className="runlist__empty">No runs match the current filters.</li>
        ) : (
          runs.map((r) => {
            const time = r.startedAt ? new Date(r.startedAt).toLocaleTimeString() : "(no time)";
            const isSel = selectedRunId === r.runId;
            const hits = matchCounts.get(r.runId) ?? 0;
            return (
              <li
                key={r.runId}
                ref={isSel ? selectedItemRef : undefined}
                className={`runlist__item ${isSel ? "runlist__item--selected" : ""}`}
                onClick={() => onSelect(r.runId)}
              >
                <div className="runlist__row1">
                  <span className="runlist__ordinal">#{r.ordinal ?? "?"}</span>
                  <span className="runlist__time">{time}</span>
                  <span className="runlist__short">
                    <Mark text={r.shortRunId} query={query} />
                  </span>
                </div>
                <div className="runlist__row2">
                  <CountBadges run={r} />
                  {isSearching && hits > 0 && (
                    <span className="runlist__hits" title={`${hits} match${hits === 1 ? "" : "es"} in this run`}>
                      {hits} hit{hits === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </li>
            );
          })
        )}
      </ul>
    </aside>
  );
}

function CountBadges({ run }: { run: RunSummary }) {
  const items: { key: keyof RunSummary["counts"]; cls: string }[] = [
    { key: "block", cls: "badge--block" },
    { key: "review", cls: "badge--review" },
    { key: "none", cls: "badge--none" },
    { key: "skip", cls: "badge--skip" },
    { key: "error", cls: "badge--error" },
    { key: "filtered", cls: "badge--filtered" },
  ];
  const nonzero = items.filter((i) => run.counts[i.key] > 0);
  if (nonzero.length === 0) {
    return <span className="badge badge--empty">empty</span>;
  }
  return (
    <>
      {nonzero.map((i) => (
        <span key={i.key} className={`badge ${i.cls}`} title={i.key}>
          {run.counts[i.key]} {i.key}
        </span>
      ))}
    </>
  );
}
